"use client";

import * as React from "react";
import {
  BackendMultiSelect,
  type BackendMultiSelectProps,
} from "./backend-multi-select";

/**
 * Props for the BackendSingleSelect component
 *
 * Clean wrapper around BackendMultiSelect for single value selection
 * Provides proper typing for single value scenarios
 */
export interface BackendSingleSelectProps
  extends Omit<
    BackendMultiSelectProps,
    | "selectedValues"
    | "onSelectionChange"
    | "maxSelections"
    | "returnSingleValue"
  > {
  /** Currently selected option value (single string or null) */
  selectedValue: string | null;
  /** Callback function called when selection changes (single string or null) */
  onSelectionChange: (value: string | null) => void;
}

/**
 * BackendSingleSelect - Type-safe single value selector
 *
 * Clean wrapper around BackendMultiSelect that enforces single value selection
 * with proper TypeScript typing. Handles conversion between single values and arrays internally.
 *
 * Usage:
 * ```tsx
 * <BackendSingleSelect
 *   selectedValue={makeId}  // string | null
 *   onSelectionChange={(value) => setMakeId(value)}  // (string | null) => void
 *   options={makeOptions}
 *   label="Marke"
 *   required
 * />
 * ```
 */
export const BackendSingleSelect = React.forwardRef<
  HTMLButtonElement,
  BackendSingleSelectProps
>(function BackendSingleSelect(
  { selectedValue, onSelectionChange, ...props }: BackendSingleSelectProps,
  triggerRef
) {
  // Convert single value to array for BackendMultiSelect
  const selectedValues = selectedValue ? [selectedValue] : [];

  // Convert array back to single value
  const handleSelectionChange = (values: string[]) => {
    const newValue = values.length > 0 ? values[0] : null;
    onSelectionChange(newValue);
  };

  return (
    <BackendMultiSelect
      {...props}
      selectedValues={selectedValues}
      onSelectionChange={handleSelectionChange}
      maxSelections={1}
      returnSingleValue={true}
      closeOnSelect={true}
      ref={triggerRef}
    />
  );
});

BackendSingleSelect.displayName = "BackendSingleSelect";
